import { Carer, Resident, ResidentStatus, User } from '@lmc/ts-common'
import { Avatar } from '@lmc/ui-kit'
import useResidentsList from 'hooks/useResidentsList'
import useUsersList from 'hooks/useUser/useUsersList'
import { useTranslation } from 'react-i18next'
import { useCarers } from 'screens/Organisation/screens/TeamList'

const getName = (item: { name?: { first?: string; last?: string } }) =>
  `${item?.name?.first} ${item?.name?.last}`

export const useGroupedResidents = () => {
  const { residents } = useResidentsList({})

  const groupedResidents: { [status: string]: Resident[] } = {
    [ResidentStatus.active]: [],
    [ResidentStatus.inactive]: [],
    [ResidentStatus.hospitalised]: [],
    [ResidentStatus.deceased]: [],
    [ResidentStatus.discharged]: [],
  }

  residents?.forEach((resident) => {
    const { status } = resident as unknown as Resident
    if (!groupedResidents[status]) {
      groupedResidents[status] = []
    }
    groupedResidents[status].push(resident as unknown as Resident)
  })

  Object.keys(groupedResidents).forEach((key) => {
    if (groupedResidents[key].length === 0) {
      delete groupedResidents[key]
    } else {
      groupedResidents[key].sort((a, b) => {
        const nameA = getName(a)
        const nameB = getName(b)
        return nameA.localeCompare(nameB)
      })
    }
  })

  const groupedResidentOptions = Object.keys(groupedResidents).map(
    (status) => ({
      label: status.toLocaleUpperCase(),
      options: groupedResidents[status].map((resident) => ({
        label: getName(resident),
        value: resident.id,
        image: <Avatar url={resident.picture as unknown as string} size={20} />,
      })),
    })
  )

  return { groupedResidentOptions }
}

export const useGroupedCarers = (excludeGroupUsers?: boolean) => {
  const { carers } = useCarers(excludeGroupUsers)

  const groupedCarers: { [status: string]: Carer[] } = {
    active: [],
    inactive: [],
  }

  carers
    ?.filter(
      (carer) =>
        !carer?.deleted &&
        !(carer as unknown as Carer & { hidden?: boolean })?.hidden
    )
    .forEach((carer) => {
      if (carer.active) {
        groupedCarers.active.push(carer as unknown as Carer)
      } else {
        groupedCarers.inactive.push(carer as unknown as Carer)
      }
    })

  Object.keys(groupedCarers).forEach((key) => {
    if (groupedCarers[key].length === 0) {
      delete groupedCarers[key]
    } else {
      groupedCarers[key].sort((a, b) => {
        const nameA = getName(a)
        const nameB = getName(b)
        return nameA.localeCompare(nameB)
      })
    }
  })

  const groupedCarerOptions = Object.keys(groupedCarers).map((status) => ({
    label: status.toLocaleUpperCase(),
    options: groupedCarers[status].map((carer) => ({
      label: getName(carer),
      value: carer.id,
      image: <Avatar url={carer.picture as unknown as string} size={20} />,
    })),
  }))

  return { groupedCarerOptions }
}

export const useGroupedUsers = () => {
  const { users } = useUsersList({})
  const { t } = useTranslation()

  const groupedUsers: { [status: string]: User[] } = {
    [t('ACTIVE')]: [],
    [t('INACTIVE')]: [],
  }

  users?.forEach((user: User) => {
    if (user.active) {
      groupedUsers[t('ACTIVE')].push(user)
    } else {
      groupedUsers[t('INACTIVE')].push(user)
    }
  })

  Object.keys(groupedUsers).forEach((key) => {
    if (groupedUsers[key].length === 0) {
      delete groupedUsers[key]
    } else {
      groupedUsers[key].sort((a, b) => {
        const nameA = getName(a)
        const nameB = getName(b)
        return nameA.localeCompare(nameB)
      })
    }
  })

  const groupedUserOptions =
    Object.keys(groupedUsers).map((status) => ({
      label: status.toLocaleUpperCase(),
      options: groupedUsers[status].map((user) => ({
        label: getName(user),
        value: user.id,
        image: <Avatar url={user.picture as unknown as string} size={20} />,
      })),
    })) || []

  return { groupedUserOptions }
}
